import { buildProfilePictureUrl, ERROR_TYPES } from "deso-protocol";
import { uniqBy } from "es-toolkit";

import { REWARD_CLAIM_STATUS } from "@/backend/focus/admin-ui";
import type {
  CoreAccountFieldsFragment,
  TokenBalanceSummariesQuery,
  TokenBalanceSummary,
} from "@/graphql/codegen/graphql";
import { tokenNanosToInteger } from "@/utils/currency";
import type { PartialAccount } from "@/utils/types";

import {
  DEFAULT_PROFILE_PIC,
  DESO_FOUNDATION_TOKENS,
  DESO_WRAPPED_ASSETS,
} from "./constants";

export type Dictionary<T> = { [key: string]: T };

export const copyTextToClipboard = async (text: string) => {
  if ("clipboard" in navigator) {
    return await navigator.clipboard.writeText(text);
  } else {
    return document.execCommand("copy", true, text);
  }
};

export const formatNumberWithSuffix = (num: number) => {
  const suffixes = ["st", "nd", "rd"];
  const numberString = num.toString();
  const lastDigit = Number.parseInt(numberString[numberString.length - 1]);

  if (
    numberString.slice(-2) === "11" ||
    numberString.slice(-2) === "12" ||
    numberString.slice(-2) === "13"
  ) {
    return numberString + "th";
  }

  const suffix = suffixes[lastDigit - 1] || "th";
  return numberString + suffix;
};

export const shortenLongWord = (
  key: string | null,
  endFirstPartAfter = 6,
  startSecondPartAfter = 6,
  separator = "...",
) => {
  if (!key || key.length <= endFirstPartAfter + startSecondPartAfter) {
    return key || "";
  }

  return [
    key.slice(0, endFirstPartAfter),
    separator,
    key.slice(-startSecondPartAfter),
  ].join("");
};

export const formatDateTime = (
  input: string | number,
  options?: Intl.DateTimeFormatOptions,
): string => {
  const inputDate = new Date(input);

  const defaults: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    formatMatcher: "basic",
    ...options,
  };

  return inputDate.toLocaleString("en-US", defaults).replace(/,\s*/, " ");
};

export const hasNFTProfilePic = (extraData?: Dictionary<string>) => {
  return !!(
    extraData?.NFTProfilePicturePostHashHex && extraData?.NFTProfilePictureUrl
  );
};

const APP_DEFAULT_PROFILE_PICTURE_PATH = "/assets/app-default-profile.png";
const FOCUS_DEFAULT_PROFILE_PICTURE_URL = `https://focus.xyz${APP_DEFAULT_PROFILE_PICTURE_PATH}`;

export const getUserProfilePicture = (
  account?: Pick<
    Partial<CoreAccountFieldsFragment>,
    "publicKey" | "username" | "extraData"
  > | null,
): string => {
  if (!account || !account?.publicKey) {
    return DEFAULT_PROFILE_PIC;
  }

  if (hasNFTProfilePic(account.extraData)) {
    return account.extraData.NFTProfilePictureUrl;
  }

  if (account.extraData?.LargeProfilePicURL) {
    return account.extraData.LargeProfilePicURL;
  }

  const profilePictureUrl = buildProfilePictureUrl(account.publicKey, {
    nodeURI: process.env.NEXT_PUBLIC_CORE_DESO_NODE,
  });

  return `${profilePictureUrl}?fallback=${encodeURIComponent(
    FOCUS_DEFAULT_PROFILE_PICTURE_URL,
  )}`;
};

export const dataURIToFile = async (
  dataURI: string,
  fileName: string,
): Promise<File> => {
  const response = await fetch(dataURI);
  const blob = await response.blob();
  return new File([blob], fileName, {
    type: normalizeImageMimeType(blob.type),
  });
};

export const normalizeImageMimeType = (mimeType?: string) => {
  if (!mimeType) {
    return mimeType || "";
  }

  return mimeType === "image/jpg" ? "image/jpeg" : mimeType;
};

interface PluralizeOptions {
  // Can be used to override irregular plural suffix rules
  irregularSuffix?: string;
  // If passed it will completely override the word. Useful for words that don't follow the standard pluralization rules
  // such as "person" -> "people" and the like.
  irregularWord?: string;
  // If true, the localized, formatted count will be included in the output. For example, "1,000 followers" or "1 follower", etc. This is true by default.
  includeCount?: boolean;
}

const irregularPlurals: { [key: string]: string } = {
  person: "people",
  child: "children",
  man: "men",
  woman: "women",
  tooth: "teeth",
  foot: "feet",
  mouse: "mice",
  goose: "geese",
};

export const pluralize = (
  count: number,
  word: string,
  options: Partial<PluralizeOptions> = {},
) => {
  let pluralizedResult: string;
  const defaultOptions = { includeCount: true };
  const { irregularSuffix, irregularWord, includeCount } = {
    ...defaultOptions,
    ...options,
  };

  if (count === 1) {
    pluralizedResult = word;
  } else if (irregularWord) {
    pluralizedResult = irregularWord;
  } else if (irregularPlurals[word.toLowerCase()]) {
    pluralizedResult = irregularPlurals[word.toLowerCase()];
  } else if (irregularSuffix) {
    pluralizedResult = `${word.slice(0, -1)}${irregularSuffix}`;
  } else if (/y$/.test(word) && !/[aeiou]y$/.test(word)) {
    pluralizedResult = `${word.slice(0, -1)}ies`;
  } else if (/(ch|s|sh|x|z)$/.test(word)) {
    pluralizedResult = `${word}es`;
  } else {
    pluralizedResult = `${word}s`;
  }

  return includeCount
    ? `${count.toLocaleString()} ${pluralizedResult}`
    : pluralizedResult;
};

export const concatUserResponses = (
  exactMatch?: PartialAccount | null,
  usersList?: Array<PartialAccount | null>,
): Array<PartialAccount> => {
  const nonEmptyUsers = (usersList || []).filter((e) => e !== null);

  return uniqBy(
    [exactMatch, ...nonEmptyUsers].filter((e) => !!e && e.publicKey),
    (x) => x?.publicKey,
  ).filter(Boolean);
};

const publicKeyRegexMainnet = /^BC[1-9A-HJ-NP-Za-km-z]{53}$/;
const publicKeyRegexTestnet = /^tBC[1-9A-HJ-NP-Za-km-z]{51}$/;

export const isDesoPublicKey = (value: string) => {
  return publicKeyRegexMainnet.test(value) || publicKeyRegexTestnet.test(value);
};

export const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

const ABORT_ERROR_MESSAGE_REGEX =
  /\b(abort(?:ed|ing)?|cancel(?:ed|led|ing)?)\b/i;

const isAbortErrorInternal = (error: unknown, depth = 0): boolean => {
  if (!error || depth > 3) {
    return false;
  }

  if (error instanceof DOMException) {
    return error.name === "AbortError";
  }

  if (error instanceof Error) {
    return (
      error.name === "AbortError" ||
      ABORT_ERROR_MESSAGE_REGEX.test(error.message) ||
      isAbortErrorInternal(
        (error as Error & { cause?: unknown }).cause,
        depth + 1,
      )
    );
  }

  if (typeof error !== "object") {
    return false;
  }

  const abortLikeError = error as {
    cause?: unknown;
    error?: unknown;
    message?: unknown;
    name?: unknown;
    networkError?: unknown;
  };

  return (
    abortLikeError.name === "AbortError" ||
    (typeof abortLikeError.message === "string" &&
      ABORT_ERROR_MESSAGE_REGEX.test(abortLikeError.message)) ||
    isAbortErrorInternal(abortLikeError.cause, depth + 1) ||
    isAbortErrorInternal(abortLikeError.networkError, depth + 1) ||
    isAbortErrorInternal(abortLikeError.error, depth + 1)
  );
};

export const isAbortError = (error: unknown): error is Error =>
  isAbortErrorInternal(error);

const MEDIA_EVENT_ERROR_MESSAGES: Record<number, string> = {
  1: "Media loading was aborted before the preview was ready.",
  2: "A network error prevented the media preview from loading.",
  3: "The browser could not decode the selected media file.",
  4: "The selected media format is not supported by this browser.",
};

const parseBrowserEventError = (error: unknown): string | null => {
  if (Array.isArray(error)) {
    for (const item of error) {
      const parsed = parseBrowserEventError(item);
      if (parsed) {
        return parsed;
      }
    }

    return null;
  }

  if (typeof error !== "object" || error === null || !("isTrusted" in error)) {
    return null;
  }

  const eventLike = error as {
    currentTarget?: {
      error?: { code?: number; message?: string } | null;
    } | null;
    target?: {
      error?: { code?: number; message?: string } | null;
    } | null;
    type?: unknown;
  };

  const eventTargets = [eventLike.target, eventLike.currentTarget];

  for (const target of eventTargets) {
    const targetError = target?.error;

    if (!targetError) {
      continue;
    }

    if (
      typeof targetError.message === "string" &&
      targetError.message.trim().length > 0
    ) {
      return targetError.message;
    }

    if (
      typeof targetError.code === "number" &&
      MEDIA_EVENT_ERROR_MESSAGES[targetError.code]
    ) {
      return MEDIA_EVENT_ERROR_MESSAGES[targetError.code];
    }
  }

  if (eventLike.type === "error") {
    return "The browser could not read the selected file.";
  }

  return "The browser reported an unknown file-read error.";
};

/**
 * Sometimes we get a native Error instance, sometimes we get a string,
 * sometimes we get a JSON object from the server.  This function tries to parse
 * the error and return as much information as possible regardless of the type
 * of error object.
 * @param error
 * @returns
 */
type ParseErrorOptions = {
  log?: boolean;
};

export const parseError = (
  error: unknown,
  { log = true }: ParseErrorOptions = {},
): string => {
  if (error === null) {
    return "";
  }

  if (isAbortError(error)) {
    return error.message;
  }

  if (typeof error === "string") {
    if (log) {
      console.error(error);
    }
    return error;
  }

  const browserEventError = parseBrowserEventError(error);

  if (browserEventError) {
    if (log) {
      console.error(browserEventError);
    }
    return browserEventError;
  }

  let message = "";

  if (
    typeof error === "object" &&
    "message" in error &&
    typeof error.message === "string"
  ) {
    message += error.message;
  }

  if (typeof error === "object" && "response" in error) {
    const axiosError = error as { response: { data: { error: string } } };
    if (axiosError.response?.data?.error) {
      message += `: ${axiosError.response.data.error}`;
    }
  }

  const results = message.length > 0 ? message : JSON.stringify(error);

  if (log) {
    console.error(results);
  }

  return results;
};

export function abbreviateNumber(
  value: number | string,
  formatterOptions?: Intl.NumberFormatOptions,
): string {
  const casterNumberValue = Number(value);

  if (Number.isNaN(casterNumberValue)) {
    return "0";
  }

  if (casterNumberValue > 1e20) {
    return abbreviateBigNumber(casterNumberValue);
  }

  return Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 2,
    ...formatterOptions,
  }).format(casterNumberValue);
}

export function abbreviateBigNumber(value: number) {
  const scientific = Number(value).toExponential(1); // Use 1 digit after the decimal
  return scientific.length <= 7 ? scientific : scientific.slice(0, 7); // Ensure <= 7 characters
}

export const isIdentityWindowClosedError = (e: unknown) => {
  return (
    typeof e === "object" &&
    e !== null &&
    "type" in e &&
    e.type === ERROR_TYPES.IDENTITY_WINDOW_CLOSED
  );
};

export const isAccessGroupAlreadyExistsError = (error: unknown) => {
  return parseError(error).includes("Pay To Unlock already exists");
};

export const getEnumKeyByValue = <T extends object>(
  enumObj: T,
  value: string | number,
): keyof T | undefined => {
  return (Object.keys(enumObj) as Array<keyof T>).find(
    (key) => enumObj[key] === value,
  );
};

export const isRewardClaimEditable = (currentStatus: REWARD_CLAIM_STATUS) => {
  return [
    REWARD_CLAIM_STATUS.PENDING,
    REWARD_CLAIM_STATUS.REJECTED,
    REWARD_CLAIM_STATUS.APPROVED,
    REWARD_CLAIM_STATUS.MANUAL_APPROVAL_REQUIRED,
  ].includes(currentStatus);
};

export const extractWrappedAssetBalance = (
  data: TokenBalanceSummariesQuery,
  wrappedAssetPublicKey: string,
) => {
  const tokenBalance = (data.tokenBalanceSummaries?.nodes || []).find(
    (e) => e?.creator?.publicKey === wrappedAssetPublicKey,
  ) as TokenBalanceSummary | null;

  return tokenNanosToInteger(tokenBalance?.totalBalance ?? 0);
};

export const getPollInterval = (durationMs: number) => {
  return Number(
    process.env.NEXT_PUBLIC_GLOBAL_POLLING_INTERVAL_OVERRIDE ?? durationMs,
  );
};

export const isPWA = () => {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(display-mode: standalone)").matches
  );
};

export const isWrappedAsset = (publicKey: string) => {
  return DESO_WRAPPED_ASSETS[publicKey];
};

export const isWrappedAssetByValue = (value: string) => {
  for (const [key, val] of Object.entries(DESO_WRAPPED_ASSETS)) {
    if (val === value) {
      return key;
    }
  }
  return null;
};

export const isDesoFoundationToken = (publicKey: string) => {
  return DESO_FOUNDATION_TOKENS[publicKey];
};

export const truncate = (
  str: string,
  options: {
    length: number;
    omission?: string;
    separator?: string | RegExp;
  },
) => {
  const { length, omission = "...", separator } = options;

  // Convert to string and handle empty/null cases
  if (!str) return "";

  // Get proper string length accounting for Unicode
  const strLength = [...str].length;

  // Return original string if it's shorter than truncation length
  if (length >= strLength) {
    return str;
  }

  // Calculate end position considering omission length
  const end = length - [...omission].length;

  // Handle cases where omission is longer than desired length
  if (end < 1) {
    return omission;
  }

  let result = [...str].slice(0, end).join("");

  // Handle simple truncation without separator
  if (separator === undefined) {
    return result + omission;
  }

  // Handle RegExp separator
  if (separator instanceof RegExp) {
    const searchRegex = new RegExp(
      separator.source,
      (separator.flags || "") + (separator.global ? "" : "g"),
    );

    let match;
    let lastIndex = -1;

    searchRegex.lastIndex = 0;
    while ((match = searchRegex.exec(result))) {
      lastIndex = match.index;
    }

    if (lastIndex > -1) {
      result = result.slice(0, lastIndex);
    }
  }
  // Handle string separator
  else {
    const index = result.lastIndexOf(separator as string);
    if (index > -1) {
      result = result.slice(0, index);
    }
  }

  return result + omission;
};

export function update<T>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  obj: Record<string, any>,
  path: string | string[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updater: (currentValue: any) => T,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Record<string, any> {
  if (typeof path === "string") {
    path = path.split(".");
  }

  let current = obj;

  for (let i = 0; i < path.length; i++) {
    const key = path[i];

    if (i === path.length - 1) {
      current[key] = updater(current[key]);
    } else {
      if (current[key] === undefined) {
        current[key] = {};
      }
      current = current[key];
    }
  }

  return obj;
}
